/* Product photos arrive once, when ready to paint. Text and controls stay still. */
(() => {
  if (!window.customElements || customElements.get('vinus-product-media')) return;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const records = new Map();
  const allowed = () => !reduced.matches && !window.Shopify?.designMode && !document.hidden;
  const live = (element, record) => element.isConnected && records.get(element) === record;
  const visible = element => {
    const box = element.getBoundingClientRect();
    return box.top < window.innerHeight && box.bottom > 0 && box.left < window.innerWidth && box.right > 0;
  };

  function settle(element) {
    const record = records.get(element);
    if (!record) return;
    record.settled = true;
    element.revealed = true;
    clearTimeout(record.timer);
    element.classList.remove('vinus-product-pending', 'vinus-product-arriving');
    observer?.unobserve(element);
  }
  function reveal(element, record) {
    if (!live(element, record) || record.settled || !record.ready || !record.intersecting ||
        record.readySource !== (record.primary.currentSrc || record.primary.src)) return;
    settle(element);
    if (allowed() && !record.card.contains(document.activeElement)) {
      element.classList.add('vinus-product-arriving');
      // Also release the animation if animationend is lost (e.g. a stylesheet update).
      record.timer = setTimeout(() => { if (live(element, record)) settle(element); }, 600);
    }
  }
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(({ target, isIntersecting }) => {
      const record = records.get(target);
      if (!record || record.settled || !live(target, record)) return;
      record.intersecting = isIntersecting;
      if (!isIntersecting) return;
      // Lazy offscreen images do not time out before they have been requested.
      record.timer ??= setTimeout(() => { if (live(target, record)) settle(target); }, 8000);
      reveal(target, record);
    });
  }, { threshold: 0 }) : null;

  // Every callback belongs to one connection. A removed/replaced section cannot
  // be changed by an old load, decode, observer or timeout callback.
  function watch(element, record, image, ready, fail, reset = () => {}) {
    const source = () => image.currentSrc || image.src;
    let generation = 0;
    let decodingSource;
    const error = () => {
      generation++;
      decodingSource = undefined;
      if (live(element, record)) fail();
    };
    const load = () => {
      if (!live(element, record)) return;
      const selectedSource = source();
      if (decodingSource === selectedSource && generation) return;
      if (!image.naturalWidth) { error(); return; }
      decodingSource = selectedSource;
      const attempt = ++generation;
      reset();
      const current = () => live(element, record) && generation === attempt && source() === selectedSource;
      try {
        Promise.resolve(image.decode?.()).then(() => {
          if (current() && image.naturalWidth) ready(selectedSource);
        }, () => { if (current()) error(); });
      } catch { if (current()) error(); }
    };
    image.addEventListener('load', load);
    image.addEventListener('error', error);
    record.cleanup.push(() => {
      image.removeEventListener('load', load);
      image.removeEventListener('error', error);
    });
    if (image.complete) load();
  }

  class VinusProductMedia extends HTMLElement {
    connectedCallback() {
      if (records.has(this)) return;
      const [primary, secondary] = this.querySelectorAll('img');
      if (!primary) return;
      const record = { cleanup: [], primary, card: this.closest('.card-wrapper, .vinus-product-card') || this };
      records.set(this, record);
      const end = event => { if (event.target === this) settle(this); };
      this.addEventListener('animationend', end);
      record.cleanup.push(() => this.removeEventListener('animationend', end));
      // Never hide a photo that could already have painted during a late boot.
      // Cached photos below/right of the viewport can still arrive once safely.
      const box = this.getBoundingClientRect();
      const unpainted = box.top >= window.innerHeight || box.left >= window.innerWidth ||
        (visible(this) && !primary.complete && !primary.naturalWidth);
      if (!this.revealed && this.hasAttribute('data-vinus-reveal') && observer && allowed() && unpainted &&
          !record.card.contains(document.activeElement)) {
        this.classList.add('vinus-product-pending');
        observer.observe(this);
        watch(this, record, primary, selectedSource => {
          record.ready = true;
          record.readySource = selectedSource;
          reveal(this, record);
        }, () => settle(this), () => { record.ready = false; });
      } else settle(this);
      // The primary remains underneath this overlay in every hover/focus state.
      // Its arrival never waits for the secondary's separate request or decode.
      if (secondary) watch(this, record, secondary,
        () => this.setAttribute('data-vinus-secondary-ready', ''),
        () => this.removeAttribute('data-vinus-secondary-ready'),
        () => this.removeAttribute('data-vinus-secondary-ready'));
    }
    disconnectedCallback() {
      const record = records.get(this);
      if (!record) return;
      settle(this);
      record.cleanup.forEach(cleanup => cleanup());
      records.delete(this);
    }
  }
  customElements.define('vinus-product-media', VinusProductMedia);

  const settleAll = () => records.forEach((_record, element) => settle(element));
  reduced.addEventListener('change', settleAll);
  document.addEventListener('visibilitychange', () => { if (document.hidden) settleAll(); });
  window.addEventListener('pagehide', settleAll);
  window.addEventListener('pageshow', event => { if (event.persisted) settleAll(); });
  document.addEventListener('focusin', event => records.forEach((record, element) => {
    if (record.card.contains(event.target)) settle(element);
  }));
  ['shopify:section:unload', 'shopify:block:select', 'shopify:section:select'].forEach(name => {
    document.addEventListener(name, event => records.forEach((_record, element) => {
      if (!event.target.contains(element)) return;
      if (name === 'shopify:section:unload') element.disconnectedCallback();
      else settle(element);
    }));
  });
})();
